<script setup lang="ts">
import { injectHubRowContext } from '@/modules/Workspaces/KnowledgeHub/Hubs/FileTable/context'
import ContextActions from '@/modules/Workspaces/KnowledgeHub/Hubs/FileTable/ContextActions.vue'
import { useHubEntityStatus } from '@/modules/Workspaces/KnowledgeHub/Hubs/FileTable/useHubEntityStatus'
import { getHubFileFields } from '@/modules/Workspaces/KnowledgeHub/Hubs/getHubFileFields'
import { useHubFileTableContext } from '@/modules/Workspaces/KnowledgeHub/Hubs/hubFileTableContext'
import CheckBox from '@/uiKit/CheckBox.vue'
import CircularProgress from '@/uiKit/CircularProgress.vue'
import IconButton from '@/uiKit/IconButton.vue'
import IconSprite from '@/uiKit/IconSprite.vue'
import { useActiveElement } from '@vueuse/core'
import { computed, useTemplateRef } from 'vue'

const { item, isHovering, isUploading } = injectHubRowContext()

const selection = useHubFileTableContext()

const checkbox = useTemplateRef('checkbox')
const active = useActiveElement()

const hubFields = computed(() => {
  return getHubFileFields({
    properties: item.value.fileProject.properties,
    entity: item.value.entity,
  })
})

const { status } = useHubEntityStatus(hubFields)

const hasError = computed(() => status.value === 'error')

const label = computed(() => {
  return item.value.fileName
})

const checkboxLabel = computed(() => {
  return isSelected.value ? `Deselect ${label.value}` : `Select ${label.value}`
})

const isSelected = computed({
  get: () => selection.isSelected(item.value.entity.id),
  set: (value) => selection.setSelected(item.value.entity.id, value),
})
</script>

<template>
  <div
    class="relative flex h-full shrink grow items-center truncate"
    :class="{ 'text-text-disabled': isUploading }"
  >
    <!-- Checkbox, icon, throbber -->
    <div class="relative flex w-6 shrink-0 items-center">
      <!-- Has absolutely positioned children because checkbox needs to always be rendered for a11y -->
      <!-- Other components need to be layered it its place when needed -->
      <CircularProgress
        v-if="isUploading"
        class="absolute"
        size="xs"
      />
      <IconSprite
        v-else-if="!isHovering && !isSelected && active && !checkbox?.$el.contains(active)"
        icon="file-fill"
        size="lg"
        :name="label"
        class="absolute h-fit max-w-4 rounded-xs text-icon-subtlest"
      />

      <CheckBox
        v-if="!isUploading"
        ref="checkbox"
        :disabled="isUploading"
        :checked="isSelected"
        :class="{ 'opacity-100!': isSelected }"
        class="absolute opacity-0 group-hover:opacity-100 focus-within:opacity-100"
        :aria-label="checkboxLabel"
        @click.stop
        @change="(ev) => (isSelected = ev)"
      />
    </div>

    <!-- File name with truncation -->
    <div
      class="mr-16 w-full truncate"
      :title="label"
    >
      {{ label }}
    </div>
    <!-- Action buttons fixed at the right edge -->
    <ContextActions v-if="!isUploading">
      <template #default="{ open }">
        <div
          class="absolute right-2 flex items-center gap-0.5 rounded-md bg-surface-primary p-0.5 opacity-0 shadow-xs group-hover:opacity-100 focus-within:opacity-100"
        >
          <IconButton
            v-if="!hasError"
            :icon="open.icon"
            size="sm"
            variant="transparent"
            v-bind="open.props"
          />
        </div>
      </template>
    </ContextActions>
  </div>
</template>
